import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Form, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ActivityResponseDto } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { CrudModals } from "../components/CrudModals";
import { CrudToolbar } from "../components/CrudToolbar";
import { EditorPanel } from "../components/EditorPanel";
import { EmptyState } from "../components/EmptyState";
import { PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { LocalizedFields } from "../components/LocalizedFields";
import { TranslationStatus } from "../components/TranslationStatus";
import { useColumnSearch } from "../components/useColumnSearch";
import {
    addActivity,
    clearErrorActivity,
    deleteActivity,
    fetchActivities,
    selectActivity,
    startAddActivity,
    startDeleteActivity,
    updateActivity
} from "../features/activitiesSlice";
import { localizationEnabled } from "../config/locales";
import { activityFieldKeys, activityFields } from "../model/localizedEntities";
import { allLocalizedValues } from "../utils/translations";

export const ActivitiesPage = () => {

    const dispatch = useAppDispatch()
    const { t } = useTranslation();
    const activities = useAppSelector(state => state.activities.activities);
    const selectedActivity = useAppSelector(state => state.activities.selectedActivity);
    const status = useAppSelector(state => state.activities.status);
    const error = useAppSelector(state => state.activities.error);

    useEffect(() => {
        dispatch(fetchActivities());
    }, []);

    const columnSearch = useColumnSearch<ActivityResponseDto>({
        placeholder: t("placeholder.searchActivity"),
        getSearchValues: activity => allLocalizedValues(activity, activityFieldKeys)
    });

    const onRow = (activity: ActivityResponseDto) => ({
        onClick: () => {
            dispatch(selectActivity(activity))
        },
    });

    const columns: ColumnsType<ActivityResponseDto> = [
        {
            title: t("column.name"),
            dataIndex: 'name',
            key: 'name',
            ...columnSearch,
            sorter: (a, b) => a.name! > b.name! ? -1 : 1
        },
        ...(localizationEnabled ? [{
            width: 200,
            title: t("column.translations"),
            key: 'translations',
            render: (_: unknown, activity: ActivityResponseDto) =>
                <TranslationStatus entity={activity} fields={activityFieldKeys} />
        }] : [])
    ];

    const getRowClassName = (activity: ActivityResponseDto) => {
        return selectedActivity != null && selectedActivity.id === activity.id ? 'bg-selected' : '';
    }

    const onSaveActivity = () => {
        if (selectedActivity?.name == null || selectedActivity.name.trim().length === 0) return;

        if (selectedActivity.id == null) {
            dispatch(addActivity(selectedActivity));
            return;
        }

        dispatch(updateActivity(selectedActivity));
    }

    const canSave = selectedActivity?.name != null && selectedActivity.name.trim().length > 0;

    const addForm = status === "adding" ? (
        <EditorPanel
            title={t(selectedActivity?.id != null ? "editor.edit" : "editor.new")}
            actions={
                <>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() => dispatch(clearErrorActivity())}
                    >
                        {t("actions.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        disabled={!canSave}
                        icon={<SaveOutlined />}
                        onClick={onSaveActivity}
                    >
                        {t("actions.save")}
                    </Button>
                </>
            }
        >
            <Form name="basic" layout="vertical" autoComplete="off">
                <LocalizedFields
                    fields={activityFields}
                    value={selectedActivity ?? {}}
                    onChange={activity => dispatch(selectActivity(activity))}
                />
            </Form>
        </EditorPanel>
    ) : null;

    const content = status === 'loading' || status === 'idle'
        ? <TableSkeleton />
        : <>
            {status !== "adding" ? <CrudToolbar
                hasSelection={selectedActivity != null}
                canEdit={selectedActivity?.id != null}
                selectionLabel={selectedActivity?.name}
                onDelete={() => { if (selectedActivity != null) dispatch(startDeleteActivity()); }}
                onEdit={() => { if (selectedActivity != null) dispatch(startAddActivity(selectedActivity)); }}
                onAdd={() => dispatch(startAddActivity({} as ActivityResponseDto))}
                onReload={() => dispatch(fetchActivities())}
                onClearSelection={() => dispatch(selectActivity(undefined))}
            /> : null}
            {addForm}
            <div className="card card--flush">
                <Table
                    rowKey={(activity: ActivityResponseDto) => activity.id!}
                    rowClassName={getRowClassName}
                    onRow={onRow}
                    dataSource={activities}
                    columns={columns}
                    locale={{ emptyText: <EmptyState /> }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                />
            </div>

            <CrudModals
                status={status}
                error={error}
                entityKey="activity"
                deleteTarget={selectedActivity?.name}
                onDismiss={() => dispatch(clearErrorActivity())}
                onConfirmDelete={() => { if (selectedActivity != null) dispatch(deleteActivity(selectedActivity.id!)); }}
            />
        </>

    return (
        <>
            <PageHeader
                title={t("page.activities.title")}
                count={activities?.length}
            />
            {content}
        </>
    );
}
